import { setAllowedAgents } from './allowedAgents'
import { setTableCellData } from './tableCell'
import { setAllowedPhotos } from './allowedPhotos'
import { setAllowedPermissionTabs } from './allowedTabs'
import { setChangeAgent } from './changeAgent'
import { setEditData } from './editData'
import { setExportData } from './exportData'
import { setExportSyncWidget } from './exportSync'
import { setGoogleMaps } from './googleMaps'
import { setModulePermissions } from './module'
import { setNavigation } from './navigation'
import { setPropertyStatus } from './propertyStatus'
import { setSubMenu } from './subMenu'
import { setViewData } from './viewData'

const setEditPermissions = (isAgentOwnership, isAgentOffice, actionName, module, level, viewLevel) => {
    const isAffectedModule = ['contacts', 'companies'].includes(module)

    const permissions = {
        read_only: ['view'],
        read_write_delete: ['view', 'edit', 'delete'],
    }

    const allowedActions = permissions[level]

    if (isAgentOwnership) {
        return true
    }

    if (!allowedActions) {
        console.log(`Non existing edit level: ${level}`)
        return false
    }

    const hasEditPermissions = allowedActions.includes(actionName)

    // special case of edit permissions is applied for modules Contacts & Companies
    // since they are not filtered like Projects & Properties
    const contactsCompaniesEditPermissions = {
        all: hasEditPermissions,
        office: hasEditPermissions && isAgentOffice,
        own: false,
    }

    const hasContactsCompaniesEditPermissions = contactsCompaniesEditPermissions[viewLevel]

    return isAffectedModule ? hasContactsCompaniesEditPermissions : hasEditPermissions
}

// applied only for Contacts/Companies modules
const checkViewLevel = (permissionsArray, module) => {
    if (!module) return

    // module comes in plural
    const moduleMapping = {
        contacts: 'contact',
        companies: 'company',
    }

    const formattedModuleName = moduleMapping[module]

    const viewPermissions = ['module.contact.view', 'module.company.view']
    const permission = viewPermissions.find((p) => p.includes(formattedModuleName))

    return checkPermissionLevel(permissionsArray, permission)
}

// helper function for getAgentIdFromItem & getAgentOfficeIdFromItem
const getAgentData = (item, module, id) => {
    if (!module || !id) return null

    // primaryAgent (object) exists for properties; agent covers projects (array) and contacts/companies (object)
    const agent = item?.primaryAgent || item?.agent

    if (!agent) return null

    return { agent }
}

export const checkPermissionLevel = (permissionsArray, permissionName) => {
    const permission = permissionsArray?.find((obj) => obj.permission.token === permissionName)
    return permission ? permission.permissionLevel.token : null
}

export const generatePermissionsData = (permissions, agentPermissions, isAgentOwnership, isAgentOffice) => {
    return permissions.reduce((acc, permission) => {
        const { ruleName, dataName } = permission
        const result = setPermissions(agentPermissions, ruleName, isAgentOwnership, isAgentOffice)

        acc = { ...acc, [dataName]: result }
        return acc
    }, {})
}

export const setPermissions = (agentPermissions, permission, isAgentOwnership, isAgentOffice, module, actionName = '') => {
    if (!agentPermissions) return

    const level = checkPermissionLevel(agentPermissions, permission)
    const viewLevel = checkViewLevel(agentPermissions, module)

    switch (level) {
        case 'all':
        case 'yes':
            return true
        case 'office':
            return isAgentOffice
        case 'no':
            return false
        case 'own':
            return isAgentOwnership
        case 'read_only':
        case 'read_write_delete':
            return setEditPermissions(isAgentOwnership, isAgentOffice, actionName, module, level, viewLevel)
        case 'export.all':
            return 'all'
        case 'export.category':
            return 'category'
        default:
            // uncomment line below if something is breaking to get more info
            // console.error(`Non existing permission level: ${level}`)
            return false
    }
}

// checks for agent ownership (is item created by current agent) over view, list, activity or document
export const checkAgentOwnership = (viewAgentId, currentAgentId, itemAgentId, activityAgentId, documentAgentId) => {
    const projectsAgentViewCheck = Array.isArray(viewAgentId) && viewAgentId?.some((obj) => obj.id === currentAgentId)
    const singleAgentViewCheck = viewAgentId === currentAgentId // properties, contacts & companies
    const listAgentCheck = itemAgentId === currentAgentId
    const activityAgentCheck = activityAgentId === currentAgentId
    const documentAgentCheck = documentAgentId === currentAgentId

    return listAgentCheck || projectsAgentViewCheck || singleAgentViewCheck || activityAgentCheck || documentAgentCheck
}

// checks for agent office (is item created by agent of same office) over view, list or document
export const checkAgentOffice = (viewAgentOfficeId, currentOffice, itemAgentOfficeId) => {
    const projectsOfficeViewCheck = Array.isArray(viewAgentOfficeId) && viewAgentOfficeId?.some((obj) => obj.id === currentOffice?.id)
    const singleOfficeViewCheck = viewAgentOfficeId === currentOffice?.id // properties, contacts & companies
    const listOfficeCheck = itemAgentOfficeId === currentOffice?.id
    const documentOfficeCheck = itemAgentOfficeId === currentOffice?.id

    return listOfficeCheck || projectsOfficeViewCheck || singleOfficeViewCheck || documentOfficeCheck
}

export const getAgentIdFromItem = (item, module, id) => {
    const agent = getAgentData(item, module, id)?.agent

    if (!agent) return null

    return Array.isArray(agent) ? agent.map((a) => ({ id: a.id })) : agent?.id
}

export const getAgentOfficeIdFromItem = (item, module, id) => {
    const agent = getAgentData(item, module, id)?.agent

    if (!agent) return null
    
    return Array.isArray(agent) ? agent.map((a) => ({ id: a.agencyOffice?.id })) : agent?.agencyOffice?.id
}

export {
    setAllowedAgents,
    setAllowedPermissionTabs,
    setAllowedPhotos,
    setChangeAgent,
    setEditData,
    setExportData,
    setExportSyncWidget,
    setGoogleMaps,
    setModulePermissions,
    setNavigation,
    setPropertyStatus,
    setSubMenu,
    setTableCellData,
    setViewData,
}
